import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mockVolunteerOpportunities as mockVolunteerOpportunitiesEn } from "@/mocks/volunteers";
import { mockVolunteerOpportunities as mockVolunteerOpportunitiesPs } from "@/mocks/ps/volunteers";
import { mockVolunteerOpportunities as mockVolunteerOpportunitiesDa } from "@/mocks/da/volunteers";

const localizedOpportunities: Record<string, typeof mockVolunteerOpportunitiesEn> = {
  en: mockVolunteerOpportunitiesEn,
  fr: mockVolunteerOpportunitiesEn,
  de: mockVolunteerOpportunitiesEn,
  ps: mockVolunteerOpportunitiesPs,
  da: mockVolunteerOpportunitiesDa,
};

export default function VolunteerPreview() {
  const { t, i18n } = useTranslation();
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const mockVolunteerOpportunities = localizedOpportunities[i18n.language] || mockVolunteerOpportunitiesEn;
  const featured = mockVolunteerOpportunities.slice(0, 3);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (leftRef.current) {
      leftRef.current.classList.add("reveal-left");
      observer.observe(leftRef.current);
    }

    if (rightRef.current) {
      const children = Array.from(rightRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 130}ms`;
      });
      observer.observe(rightRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left */}
          <div className="lg:col-span-2" ref={leftRef}>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">
              {t("volunteer.tag")}
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#1B2838] leading-tight mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("volunteer.title")}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-5">{t("volunteer.body1")}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">{t("volunteer.body2")}</p>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1B2838] text-white rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2a3a4a] transition-colors"
            >
              <i className="ri-hand-heart-line"></i>
              {t("volunteer.cta")}
            </Link>
            <div className="mt-4">
              <a href="#" className="text-[#4ECDC4] text-sm hover:underline cursor-pointer">
                {t("volunteer.otherWays")} &rarr;
              </a>
            </div>
          </div>

          {/* Right: Cards */}
          <div className="lg:col-span-3 flex flex-col gap-4" ref={rightRef}>
            {featured.map((opp) => (
              <div
                key={opp.id}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-[#4ECDC4]/40 transition-all duration-300 flex items-start gap-4"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#4ECDC4]/15 text-[#4ECDC4] flex-shrink-0">
                  <i className="ri-user-heart-line text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h3 className="font-bold text-[#1B2838] text-base leading-tight">{opp.title}</h3>
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      {opp.commitment}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-400 text-xs mb-2">
                    <i className="ri-map-pin-line"></i>
                    <span>{opp.location}</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                    {opp.description}
                  </p>
                </div>
                <Link
                  to="/volunteer"
                  className="text-[#4ECDC4] text-sm font-semibold whitespace-nowrap cursor-pointer hover:text-[#3dbdb5] flex items-center gap-1 flex-shrink-0 self-center"
                >
                  {t("volunteer.apply")} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}